import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, TagColored } from '@spark';
import { DEMO_TOUR_PATH } from '../demo/demoRoute';
import tourIllustration from '@/shared/assets/illustrations/walkthrough-choice-tour.svg';
import jumpIllustration from '@/shared/assets/illustrations/walkthrough-choice-jump.svg';
import './WalkthroughChoiceScreen.css';

export const WALKTHROUGH_CHOICE_TOUR = Object.freeze({
    title: 'Take the guided tour',
    body: ['See every step before you build.', 'Takes about 3 minutes.'],
    cta: 'Show me how',
    tag: 'Recommended',
});

export const WALKTHROUGH_CHOICE_JUMP = Object.freeze({
    title: 'Jump right in',
    body: ['Build your first SLM now.', 'The guide is here whenever you need it.'],
    cta: 'Start building',
});

/** `variant` ('tour' | 'jump') picks the illustration's size and placement in CSS. */
function ChoiceCard({ option, illustrationSrc, variant, onSelect, testId, primaryActionDisableWhenReadOnly }) {
    return (
        <section className="walkthrough-choice__card" data-testid={testId} aria-labelledby={`${testId}-title`}>
            <div className="walkthrough-choice__content">
                <div className={`walkthrough-choice__illustration walkthrough-choice__illustration--${variant}`}>
                    <img src={illustrationSrc} alt="" />
                </div>
                <div className="walkthrough-choice__text">
                    {option.tag ? (
                        <TagColored label={option.tag} variant="pink" className="walkthrough-choice__tag" />
                    ) : null}
                    <h2 id={`${testId}-title`} className="walkthrough-choice__title">
                        {option.title}
                    </h2>
                    <p className="walkthrough-choice__body">
                        {option.body.map((line, index) => (
                            <React.Fragment key={line}>
                                {index > 0 ? <br /> : null}
                                {line}
                            </React.Fragment>
                        ))}
                    </p>
                </div>
            </div>
            <Button
                variant="primary"
                size="large"
                disableWhenReadOnly={primaryActionDisableWhenReadOnly}
                onClick={onSelect}
                className="walkthrough-choice__cta"
            >
                {option.cta}
            </Button>
        </section>
    );
}

/**
 * Choice between the guided demo tour and today's flow (Figma 9370:2704).
 * Replaces the projects empty state for as long as the workspace has no
 * tasks, so the tour can be taken again from here at any time.
 *
 * - "Take the guided tour" opens the wizard in demo mode
 *   (`/create-task?tour=demo`): a client-side mock that calls no API and
 *   persists nothing, so it needs none of the create-task gates.
 * - "Jump right in" hands off to `onCreateProject({ assistant: true })` (the
 *   same gate the empty-state CTA uses — providers onboarding, then
 *   `/create-task?assistant=1`), which opens the wizard with the Spark
 *   assistant panel as its only guide (no coach marks).
 *
 * Neither choice is recorded: the demo is ephemeral and the real wizard
 * writes no walkthrough state.
 */
export default function WalkthroughChoiceScreen({
    onCreateProject,
    tourIllustrationSrc = tourIllustration,
    jumpIllustrationSrc = jumpIllustration,
    primaryActionDisableWhenReadOnly = true,
}) {
    const navigate = useNavigate();

    const takeTour = useCallback(() => navigate(DEMO_TOUR_PATH), [navigate]);
    const jumpIn = useCallback(() => onCreateProject?.({ assistant: true }), [onCreateProject]);

    return (
        <div className="walkthrough-choice" data-testid="walkthrough-choice">
            <ChoiceCard
                option={WALKTHROUGH_CHOICE_TOUR}
                illustrationSrc={tourIllustrationSrc}
                variant="tour"
                onSelect={takeTour}
                testId="walkthrough-choice-tour"
                primaryActionDisableWhenReadOnly={primaryActionDisableWhenReadOnly}
            />
            <ChoiceCard
                option={WALKTHROUGH_CHOICE_JUMP}
                illustrationSrc={jumpIllustrationSrc}
                variant="jump"
                onSelect={jumpIn}
                testId="walkthrough-choice-jump"
                primaryActionDisableWhenReadOnly={primaryActionDisableWhenReadOnly}
            />
        </div>
    );
}
